import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';
import {centsToCurrencyString} from '../../../utils/numbers';
import { getCandybarSelectedItemId, isCandybarFavorite, isUserLogged } from '../../../reducers';
import BackButton from '../../common/BackButton';
import CandybarItemConfigs from './CandybarItemConfigs';
import CandybarItemComments from './CandybarItemComments';
import Button from '../../Button';
import { getCandybarItemPriceWithSelectedOptions } from '../../../utils/candybar';
import { breakpoints } from '../../../utils/styles';

const Head = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 0.5rem;

  body.theme-market & { margin-bottom: 1rem; }
`;

const Title = styled.h1`
  font-size: ${(50/12.6).toFixed(2)}rem !important;
  margin-bottom: 0;

  body.theme-platinum & {
    font-size: ${(35/12.6).toFixed(2)}rem !important;
    line-height: 1.25;
  }

  body.theme-market & {
    font-size: ${(40/12.6).toFixed(2)}rem !important;
    font-weight: 900;
    line-height: 1.25;
  }
`;

const FavButton = styled.button`
  background: none;
  font-size: 2rem;
  margin: 0;
  padding: 0;

  &, &:focus {
    border: 0;
    outline: none;
  }

  ${props => props.fav && 'color: var(--primary);'}
`;

const Image = styled.img`
  width: 100%;
  margin-bottom: 2em;
`;

const Content = styled.div`
  padding-bottom: 9rem;

  @media screen and (min-width: ${breakpoints.desktop}px) {
    padding-bottom: var(--pad-x);
  }
`;

const Close = styled.div`
  padding-top: var(--pad-x);
  padding-bottom: 1rem;
`;

const Bottom = styled.div`
  background: var(--page-bg);
  bottom: -1px;
  position: fixed;
  width: 100%;

  @media screen and (min-width: ${breakpoints.desktop}px) {
    position: sticky;
  }
`;

class CandybarItem extends React.Component {

  constructor(props) {
    super(props);

    this.onOptionSelect = this.onOptionSelect.bind(this);
    this.onCommentsChange = this.onCommentsChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.validate = this.validate.bind(this);

    this.state = {
      config: this.props.unit ? this.props.unit.config : {},
      comments: this.props.unit ? (this.props.unit.config.comments || '') : ''
    };
  }

  render() {
    const { item, fav } = this.props;

    return <div>
      <Content className='pad-x'>
        <Close>
          <BackButton icon='close' onBackClick={this.props.onCancel}/>
        </Close>
        {/* <Timer/> */}
        
        <Head>
          <Title className='title-1'>{item.name}</Title>
          {this.props.isUserLogged && (
            <FavButton fav={fav} onClick={() => this.props.onFavAdd(item.id, !fav)}>
              <i className={`${fav ? 'fas' : 'fal'} fa-heart`}></i>
            </FavButton>
          )}
        </Head>
        
        {item.image && <Image src={item.image} alt={`Foto de ${item.name}`}/>}
        
        {item.description && <p>{item.description}</p>}

        <CandybarItemConfigs configs={item.configs} unitConfig={this.state.config} onOptionSelect={this.onOptionSelect}/>
        <CandybarItemComments item={item} comments={this.state.config.comments} onChange={this.onCommentsChange}/>
      </Content>

      <Bottom className='pad-x pad-y'>
        <Button onClick={this.onSubmit} block primary>
          Agregar y volver a dulcería ({centsToCurrencyString(this.getPriceWithSelectedOptions())})
        </Button>
      </Bottom>
    </div>;
  }

  getPriceWithSelectedOptions() {
    const { item } = this.props;
    const unitConfig = this.state.config;
    return getCandybarItemPriceWithSelectedOptions(item, unitConfig);
  }

  onOptionSelect(config, option, value) {
    this.setState(prevState => {
      const newConfig = Object.assign({}, prevState.config);
      const optionId = String(option.id);

      if (!(config.id in newConfig)) {
        newConfig[config.id] = [];
      }

      if (value) {
        addOptionToConfig(config, optionId, newConfig);
      } else {
        removeOptionFromConfig(config, optionId, newConfig);
      }

      return {config: newConfig};
    });
  }

  onCommentsChange(value) {
    this.setState(prevState => {
      const newConfig = Object.assign({}, prevState.config);
      newConfig.comments = value;
      
      return {config: newConfig};
    });
  }

  onSubmit() {
    try {
      this.validate();
    } catch (e) {
      alert(e.message);
      return;
    }

    const { item, unit } = this.props;

    this.props.onSave(
      item,
      unit ? unit.id : null,
      this.state.config
    );
  }

  validate() {
    const { item } = this.props;
    const unit = this.state.config;

    item.configs.forEach(config => validateConfig(config, unit));
  };

}

function validateConfig(config, unit) {
  const selection = (config.id in unit) ? unit[config.id] : [];

  if (selection.length < config.min) {
    let message = 'Debes seleccionar '
        + (config.max > config.min ? 'al menos ' : '')
        + (config.min === 1 ? 'una opción' : config.min + ' opciones')
        + ' para ' + config.name;
    throw new Error(message);
  }

  if (config.max < selection.length) {
    let message = 'No puedes seleccionar más de '
        + (config.min === 1 ? 'una opción' : config.min + ' opciones')
        + ' para ' + config.name;
    throw new Error(message);
  }

  config.options.forEach(option => {
    const isOptionSelected = selection.indexOf(option.id) > -1;
    if (isOptionSelected) {
      option.subConfigs.forEach(subConfig => validateConfig(subConfig, unit));
    }
  });
}

function addOptionToConfig(config, optionId, newConfig) {
  const configId = config.id;
  const optionAlreadySelected = newConfig[configId].indexOf(optionId) > -1;

  if (optionAlreadySelected) {
    return;
  }

  newConfig[configId].push(optionId);

  const maxOptionsExceeded = newConfig[configId].length - config.max;
  if (maxOptionsExceeded > 0) {
    const removed = newConfig[configId].splice(0, maxOptionsExceeded);
    removeSubConfigs(config, removed, newConfig);
  }
}

function removeOptionFromConfig(config, optionId, newConfig) {
  const configId = config.id;
  const i = (configId in newConfig) ? newConfig[configId].indexOf(optionId) : -1;

  if (i < 0) {
    return;
  }

  const removed = newConfig[configId].splice(i, 1);
  removeSubConfigs(config, removed, newConfig);
}

function removeSubConfigs(config, removedOptions, newConfig) {
  config.options.forEach(option => {
    if (removedOptions.indexOf(option.id) < 0) {
      return;
    }

    option.subConfigs.forEach(subConfig => {
      if (subConfig.id in newConfig) {
        delete newConfig[subConfig.id];
      }
    })
  });
}

const mapStateToProps = (state, props) => ({
  candybarOptions: state.entities.candybarOptions,
  fav: props.item && isCandybarFavorite(state, props.item.id),
  isUserLogged: isUserLogged(state),
  order: state.checkoutData.order.candybar[getCandybarSelectedItemId(state)],
  selectedUnitId: state.checkoutData.candybar.selectedUnitId,
});

export default connect(
    mapStateToProps
)(CandybarItem);